import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Flame, Plus, X } from 'lucide-react';
import { format, addDays, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { ListingPicker, type PickedListing, type ListingType } from './ListingPicker';

export function FeaturedListingsManager() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PickedListing | null>(null);
  const [type, setType] = useState<ListingType>('property');
  const [tier, setTier] = useState<'hot' | 'super_hot' | 'standard'>('hot');
  const [days, setDays] = useState(30);
  const [price, setPrice] = useState('');
  const [paymentRef, setPaymentRef] = useState('');

  const { data: featured, isLoading } = useQuery({
    queryKey: ['featured-listings'],
    queryFn: async () => {
      const { data } = await supabase
        .from('featured_listings')
        .select('*')
        .order('created_at', { ascending: false });
      return data || [];
    },
  });

  const promote = async () => {
    if (!user || !selected) return;
    const endDate = addDays(new Date(), days);
    // Map commercial back to property for storage (since they share the table)
    const storedType = selected.type === 'commercial' ? 'property' : selected.type;
    const { error } = await supabase.from('featured_listings').insert({
      listing_type: storedType,
      listing_id: selected.id,
      tier,
      end_date: endDate.toISOString().slice(0, 10),
      price_paid: parseFloat(price) || 0,
      payment_ref: paymentRef || null,
      created_by: user.id,
    });
    if (error) { toast.error(error.message); return; }
    toast.success('Listing promoted');
    qc.invalidateQueries({ queryKey: ['featured-listings'] });
    setOpen(false);
    setSelected(null); setPrice(''); setPaymentRef('');
  };

  const deactivate = async (id: string) => {
    const { error } = await supabase.from('featured_listings').update({ is_active: false }).eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Promotion deactivated');
    qc.invalidateQueries({ queryKey: ['featured-listings'] });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Flame className="h-5 w-5 text-amber-500" /> Featured Listings (Hot / Super Hot)</CardTitle>
        <Button size="sm" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Promote</Button>
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-32" /> : !featured?.length ? (
          <p className="text-sm text-muted-foreground text-center py-8">No active promotions</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {featured.map((f: any) => {
                const daysLeft = differenceInDays(new Date(f.end_date), new Date());
                return (
                  <TableRow key={f.id}>
                    <TableCell className="capitalize text-xs">{f.listing_type}</TableCell>
                    <TableCell>
                      <Badge className={f.tier === 'super_hot' ? 'bg-destructive' : f.tier === 'hot' ? 'bg-amber-500' : ''}>
                        {f.tier.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{f.price_paid?.toLocaleString()} ETB</TableCell>
                    <TableCell className="text-xs">{format(new Date(f.end_date), 'MMM d')} ({daysLeft}d)</TableCell>
                    <TableCell>
                      <Badge variant={f.is_active && daysLeft >= 0 ? 'default' : 'secondary'}>
                        {f.is_active && daysLeft >= 0 ? 'Active' : 'Expired'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {f.is_active && (
                        <Button variant="ghost" size="sm" onClick={() => deactivate(f.id)}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Promote a Listing</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Select Listing</Label>
              <ListingPicker
                value={selected}
                onChange={setSelected}
                initialType={type}
                onTypeChange={t => { setType(t); setSelected(null); }}
              />
            </div>

            <div>
              <Label>Tier</Label>
              <Select value={tier} onValueChange={(v: any) => setTier(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="hot">Hot</SelectItem>
                  <SelectItem value="super_hot">Super Hot</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Days</Label>
                <Input type="number" value={days} onChange={e => setDays(Number(e.target.value))} />
              </div>
              <div>
                <Label>Price (ETB)</Label>
                <Input type="number" value={price} onChange={e => setPrice(e.target.value)} />
              </div>
              <div>
                <Label>Payment Ref</Label>
                <Input value={paymentRef} onChange={e => setPaymentRef(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={promote} disabled={!selected}>Promote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
